import { NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    OnDestroy,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
    ALLOWED_FILE_TYPES,
    MAX_FILE_SIZE_IN_BYTES,
    MAX_NUMBER_OF_FILES,
} from 'AgentApp/ts/constants/agent-file.constants';
import { AI_SMART_COMPARE_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { UserInputMessage } from 'AgentApp/ts/interfaces/agent-message.interface';
import { AgentModelOption } from 'AgentApp/ts/interfaces/agent-provider.interface';
import { SingleSelectDropdownConfig } from 'Angular/features/components/dropdowns/single-select-dropdown/interfaces/single-select-dropdown-config.interface';

import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { UrlsService } from 'Angular/services/core/urls.service';
import { DropdownHelperService } from 'Angular/services/dropdown-helper.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import { SingleSelectDropdownModule } from 'Angular/features/components/dropdowns/single-select-dropdown/single-select-dropdown.module';
import { AutoDropDirectionModule } from 'Angular/features/directives/auto-drop-direction/auto-drop-direction.module';
import { DisabledModule } from 'Angular/features/directives/disabled/disabled.module';
import { IconModule } from 'Angular/features/directives/icon/icon.module';
import { TooltipModule } from 'Angular/features/directives/popovers/tooltip/tooltip.module';

import {
    AgentSmartCompareModalComponent,
    SmartCompareSubmit,
} from 'AgentApp/ts/features/agent/components/agent-smart-compare-modal/agent-smart-compare-modal.component';
import { AudioTranscriptionInputComponent } from 'Angular/features/components/audio-transcription-input/audio-transcription-input.component';
import { getFileIcon, isAllowedFileType, isFileDragEvent } from 'AgentApp/ts/utils/agent-file';

@Component({
    selector: 'ycn-agent-user-input',
    imports: [
        IconModule,
        ReactiveFormsModule,
        TooltipModule,
        AudioTranscriptionInputComponent,
        AgentSmartCompareModalComponent,
        AutoDropDirectionModule,
        DisabledModule,
        SingleSelectDropdownModule,
        NgTemplateOutlet,
    ],
    templateUrl: './agent-user-input.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(dragover)': 'onDragOver($event)',
        '(dragleave)': 'onDragLeave($event)',
        '(drop)': 'onDrop($event)',
        '(paste)': 'onPaste($event)',
    },
})
export class AgentUserInputComponent implements AfterViewInit, OnDestroy {
    private static readonly MAX_INPUT_LENGTH = 8_000;

    private readonly _fb = inject(FormBuilder);

    private readonly _dropdownHelperService = inject(DropdownHelperService);

    private readonly _accounts = inject(AccountsService);

    private readonly _urlsService = inject(UrlsService);

    private readonly _featureService = inject(FeatureService);

    // Smart Compare is its own entitlement; agent users without it get no Smart Flows entry.
    protected readonly hasSmartCompareFeature = this._featureService.userHasFeatures([AI_SMART_COMPARE_FEATURE]);

    /**
     * Smart Compare-only mode (the user has the Smart Compare feature but not the Agent feature): the
     * text input is disabled and clicking it leads to the Y upsell page, files cannot be attached, and
     * the only way to send a message is the Smart Compare modal.
     */
    protected readonly smartCompareOnly = input<boolean>(false);

    private readonly _clientGroupFileUploadDisabled = toSignal(
        this._accounts.getClientGroupPreferences().pipe(
            map((preferences) => !!preferences.disable_agent_file_upload),
            catchError(() => of(true))
        ),
        { initialValue: true }
    );

    protected readonly fileUploadDisabled = computed(
        () => this.smartCompareOnly() || this._clientGroupFileUploadDisabled()
    );

    private readonly _hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

    protected readonly maxAudioCanvasWidth = input<number>(468);

    protected readonly showNewChatButton = input<boolean>(false);

    /**
     * Side panel layout: caps the attachment tray at three rows, spreads the button row full width
     * (tools left, send right), and swaps the "+" tools menu for a direct attach-files button since the
     * panel is too narrow for the prompt tools
     */
    protected readonly compactLayout = input<boolean>(false);

    /**
     * When false, the chat bar stops handling its own drag-and-drop (and hides its drop overlay) because
     * a parent owns a larger drop zone — e.g. the full agent page, where dropping anywhere forwards files
     * here via {@link addFiles}. The side panel leaves this true so the bar itself is the drop target.
     */
    protected readonly dragDropEnabled = input<boolean>(true);

    protected readonly modelOptions = input<AgentModelOption[]>([]);

    protected readonly selectedModelId = input<string | null>(null);

    protected readonly modelLocked = input<boolean>(false);

    protected readonly modelChange = output<string>();

    protected readonly modelDropdownConfig = computed<SingleSelectDropdownConfig>(() => ({
        list: this.modelOptions().map((option) => ({
            label: option.label,
            selectedLabel: option.short_label,
            value: option.id,
        })),
    }));

    protected readonly modelTooltip = computed(() => {
        const selectedModelId = this.selectedModelId();
        return this.modelOptions().find((o) => o.id === selectedModelId)?.label ?? 'Model';
    });

    protected readonly dropdownMenu = viewChild<ElementRef>('dropdownMenu');

    protected readonly smartCompareModal = viewChild<AgentSmartCompareModalComponent>('smartCompareModal');

    protected readonly fileInput = viewChild<ElementRef>('agentFileInput');

    protected readonly audioInput = viewChild<AudioTranscriptionInputComponent>('audioInput');

    protected readonly textInput = viewChild<ElementRef<HTMLTextAreaElement>>('textInput');

    protected openNewChat = output<void>();

    protected sendMessage = output<UserInputMessage>();

    protected openSavedPrompts = output<void>();

    protected readonly uploadedFiles = signal<File[]>([]);

    protected readonly getFileIcon = getFileIcon;

    protected textFormControl = this._fb.control('');

    private readonly _textValue = toSignal(this.textFormControl.valueChanges, { initialValue: '' });

    protected readonly hasNewline = computed(() => (this._textValue() ?? '').includes('\n'));

    protected readonly isDropdownOpen = signal(false);

    protected readonly isTranscribing = signal(false);

    protected readonly isDragOver = signal(false);

    protected readonly isInvalidInput = computed(
        () => (this._textValue() ?? '').length > AgentUserInputComponent.MAX_INPUT_LENGTH
    );

    protected readonly isOverFileLimit = computed(() => this.uploadedFiles().length > MAX_NUMBER_OF_FILES);

    protected readonly oversizedFile = computed(
        () => this.uploadedFiles().find((file) => file.size > MAX_FILE_SIZE_IN_BYTES) ?? null
    );

    protected readonly unsupportedFile = computed(
        () => this.uploadedFiles().find((file) => !isAllowedFileType(file)) ?? null
    );

    protected readonly submitButtonTooltip = computed((): string => {
        const isOverFileLimit = this.isOverFileLimit();
        const oversizedFile = this.oversizedFile();
        const unsupportedFile = this.unsupportedFile();
        const isInvalidInput = this.isInvalidInput();
        if (isOverFileLimit) {
            return `You can only upload up to ${MAX_NUMBER_OF_FILES} files`;
        }
        if (oversizedFile) {
            return `${oversizedFile.name} exceeds the maximum file size of 32MB`;
        }
        if (unsupportedFile) {
            return `${
                unsupportedFile.name
            } is not a supported file type. Supported file types: ${ALLOWED_FILE_TYPES.join(', ')}`;
        }
        if (isInvalidInput) {
            return 'The message is too long';
        }
        return 'Send message';
    });

    protected readonly isInputDisabled = computed(() => {
        const isOverFileLimit = this.isOverFileLimit();
        const oversizedFile = this.oversizedFile();
        const unsupportedFile = this.unsupportedFile();
        const isInvalidInput = this.isInvalidInput();
        const textValue = this._textValue();
        return isOverFileLimit || !!oversizedFile || !!unsupportedFile || isInvalidInput || !textValue;
    });

    ngAfterViewInit(): void {
        const dropdown = this.dropdownMenu();
        if (dropdown) {
            this._dropdownHelperService.setCloseHandler(dropdown.nativeElement, (): void => {
                if (this.isDropdownOpen()) {
                    this.isDropdownOpen.set(false);
                }
            });
        }
    }

    toggleDropdown(): void {
        this.isDropdownOpen.update((x) => !x);
    }

    openUpsell(): void {
        window.location.href = this._urlsService.AGENT.UPSELL;
    }

    openSmartCompareModal(): void {
        this.isDropdownOpen.set(false);
        const smartCompareModal = this.smartCompareModal();
        if (smartCompareModal && this.hasSmartCompareFeature) {
            smartCompareModal.open();
        }
    }

    onSmartCompareSubmit(payload: SmartCompareSubmit): void {
        if (!this.hasSmartCompareFeature) {
            return;
        }
        this.sendMessage.emit({
            text: payload.promptText,
            files: [],
            smartCompareSecurityIds: payload.securityIds,
        });
    }

    onFileChange(event: Event): void {
        const target = event.target as HTMLInputElement;
        if (target.files) {
            this.addFiles(Array.from(target.files as FileList));
        }

        this.clearFileInput();
    }

    /**
     * Appends files (from the picker or a drag-and-drop) to the upload list, de-duplicating against
     * what's already there. Size/type/count are validated reactively by the computed signals above.
     */
    addFiles(newFiles: File[]): void {
        if (this.fileUploadDisabled()) {
            return;
        }
        this.uploadedFiles.update((existing) => this.getUniqueFiles([...existing, ...newFiles]));
    }

    onDragOver(event: DragEvent): void {
        if (!this.dragDropEnabled() || !isFileDragEvent(event)) {
            return;
        }
        // Prevent default so the browser fires a `drop` event instead of navigating to/opening the file.
        event.preventDefault();
        // Cancelling the browser default above is what makes the drop a no-op; skip the highlight so the
        // chat bar offers no drop affordance.
        if (this.fileUploadDisabled()) {
            return;
        }
        const { dataTransfer } = event;
        if (dataTransfer) {
            dataTransfer.dropEffect = 'copy';
        }
        this.isDragOver.set(true);
    }

    onDragLeave(event: DragEvent): void {
        // `dragleave` also fires when moving between child elements; only clear the highlight once the
        // cursor has actually left the chat bar.
        const relatedTarget = event.relatedTarget as Node | null;
        if (!relatedTarget || !this._hostElement.nativeElement.contains(relatedTarget)) {
            this.isDragOver.set(false);
        }
    }

    onDrop(event: DragEvent): void {
        if (!this.dragDropEnabled() || !isFileDragEvent(event)) {
            return;
        }
        event.preventDefault();
        this.isDragOver.set(false);
        const droppedFiles = event.dataTransfer?.files;
        if (droppedFiles && droppedFiles.length > 0) {
            this.addFiles(Array.from(droppedFiles));
        }
    }

    /**
     * Attaches images pasted from the clipboard (screenshots, copied browser images). When the clipboard
     * also carries plain text (e.g. an Excel range, which includes both a text and an image rendition),
     * the text paste wins and nothing is attached.
     */
    onPaste(event: ClipboardEvent): void {
        const { clipboardData } = event;
        if (!clipboardData) {
            return;
        }
        const images = Array.from(clipboardData.files).filter((file) => file.type.startsWith('image/'));
        if (images.length === 0 || clipboardData.types.includes('text/plain')) {
            return;
        }
        event.preventDefault();
        this.addFiles(images);
    }

    getUniqueFiles(files: File[]): File[] {
        return files.filter(
            (file, index) =>
                files.findIndex(
                    (f) =>
                        f.name === file.name &&
                        f.size === file.size &&
                        f.type === file.type &&
                        f.lastModified === file.lastModified
                ) === index
        );
    }

    removeFile(fileIndex: number): void {
        this.uploadedFiles.update((files) => files.filter((_, i) => i !== fileIndex));
    }

    onEnter(event: KeyboardEvent): void {
        if (this.isInputDisabled()) {
            event.preventDefault();
            return;
        }

        if (!event.shiftKey) {
            event.preventDefault();
            this.submit();
        }
    }

    ngOnDestroy(): void {
        const audioInput = this.audioInput();
        if (audioInput) {
            audioInput.cancelTranscription();
        }
    }

    submit(): void {
        if (this.smartCompareOnly()) {
            return;
        }
        this.sendMessage.emit({ text: this.textFormControl.value as string, files: this.uploadedFiles() });
        this.textFormControl.setValue('');
        this.uploadedFiles.update(() => []);
    }

    /**
     * Populates the input with the given text (e.g. a selected saved prompt) without submitting, and focuses
     * it so the user can edit placeholders before sending.
     */
    setText(text: string): void {
        this.textFormControl.setValue(text);
        const textarea = this.textInput()?.nativeElement;
        if (textarea) {
            textarea.focus();
            textarea.setSelectionRange(text.length, text.length);
        }
    }

    clearFileInput(): void {
        const fileInput = this.fileInput();
        if (fileInput) {
            fileInput.nativeElement.value = '';
        }
    }

    startTranscription(): void {
        this.isTranscribing.set(true);
        // wait for component to render, then trigger transcription
        setTimeout(() => this.audioInput()?.startTranscription());
    }

    onTranscriptionCancel(): void {
        this.isTranscribing.set(false);
    }

    onTranscribedText(text: string): void {
        this.textFormControl.setValue(text.trim());
        this.isTranscribing.set(false);
    }
}
